import { Browser } from "@capacitor/browser";
import { App } from "@capacitor/app";
import { Preferences } from '@capacitor/preferences';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { siteUrl } from "src/api/config";

const REDIRECT_SCHEME = "supplyapp://auth";
class AuthManager {
  token: string | null = null;
  loginRunning = false;
  authInProgress = false;
  onToken?: (token: string) => void;
  redirectHandled = false;

  async init(onToken: (token: string) => void) {
    this.onToken = onToken;

    // В тестовом web-окружении OAuth callback открывается как обычный URL,
    // поэтому Capacitor-события appUrlOpen/getLaunchUrl не срабатывают.
    if (window.location.href.startsWith(REDIRECT_SCHEME)) {
      await this.handleRedirect(window.location.href);
      return;
    }

    // 🔥 важно: регистрируем сразу
    App.addListener("appUrlOpen", ({ url }) => {
      this.handleRedirect(url);
    });

    // если открыли по deep link
    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      await this.handleRedirect(launch.url);
      return;
    }

    // resume
    App.addListener("resume", async () => {
      await this.handleResume();
    });

    let token: string | null = null;

    try {
      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
      token = result.value;
    } catch {
      token = null;
    }

    if (token) {
      const valid = await this.checkToken(token);

      if (valid) {
        this.token = token;
        this.onToken?.(token);
        return;
      }

      await SecureStoragePlugin.remove({ key: 'accessToken' });
    }

    await this.startLogin();
  }

  async startLogin() {
    if (this.authInProgress || this.loginRunning) return;

    this.authInProgress = true;
    this.loginRunning = true;

    const existingState = await Preferences.get({ key: 'oauth_state' });

    if (existingState.value) {
      console.log("⚠️ clearing stale oauth_state");
      await Preferences.remove({ key: 'oauth_state' });
    }

    const state = crypto.randomUUID();

    await Preferences.set({
      key: 'oauth_state',
      value: state,
    });

    const redirectUri = encodeURIComponent(REDIRECT_SCHEME);
    const version = "1.2";
    const url =
      `${siteUrl}/mobile/auth/start?redirectUri=${redirectUri}&state=${state}&frontendVersion=${version}`;

    console.log('redirectUrl', url);

    await Browser.open({ url });
  }

  async handleRedirect(url: string) {
    console.log("Redirect received:", url);

    if (!url.startsWith(REDIRECT_SCHEME)) return;

    if (this.redirectHandled) return;
    this.redirectHandled = true;

    // Закрываем системный браузер только для нативного deep link.
    // HTTP callback уже открыт внутри обычной вкладки.
    if (!/^https?:\/\//i.test(url)) {
      await Browser.close();
    }

    const parsed = new URL(url);

    const code = parsed.searchParams.get("code");
    const state = parsed.searchParams.get("state");

    console.log("👉 HANDLE REDIRECT START");

    const { value: savedState } = await Preferences.get({ key: 'oauth_state' });

    console.log("code:", code);
    console.log("state:", state);
    console.log("savedState:", savedState);

    if (!code || state !== savedState) {
      console.error("OAuth state mismatch", { code, state, savedState });
      this.resetAuth();
      return;
    }

    try {
      const res = await fetch(`${siteUrl}/mobile/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      });

      if (!res.ok) {
        throw new Error("Token exchange failed");
      }

      const data = await res.json();

      this.token = data.accessToken;

      await SecureStoragePlugin.set({
        key: 'accessToken',
        value: data.accessToken,
      });

      await Preferences.remove({ key: 'oauth_state' });

      this.resetAuth();

      if (/^https?:\/\//i.test(url)) {
        window.history.replaceState(null, "", "/");
      }

      console.log("🔥 CALLING onToken");

      this.onToken?.(data.accessToken);

    } catch (err) {
      console.error("Token exchange error", err);
      this.resetAuth();
    }
  }

  async checkToken(token: string) {
    try {
      const res = await fetch(`${siteUrl}/mobile/check`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return res.ok;
    } catch {
      return false;
    }
  }

  async handleResume() {
    if (this.authInProgress) return;

    const { value: token } = await SecureStoragePlugin.get({ key: 'accessToken' });

    if (!token) return;

    const valid = await this.checkToken(token);

    if (!valid) {
      await SecureStoragePlugin.remove({ key: 'accessToken' });
      await this.startLogin();
    }
  }

  resetAuth() {
    this.loginRunning = false;
    this.authInProgress = false;
    this.redirectHandled = false;
  }
}

export const authManager = new AuthManager();
